import React, { useCallback, useEffect, useMemo, useState } from 'react';
// ClassicJournalDisplay: 横並びテーブル形式レイアウト（2025-01-31 22:00修正）
import JournalHistorySidePanel from './JournalHistorySidePanel';
import { JournalSuggestion } from './JournalEntryHistoryPanel';
import { getErrorMessage } from '../utils/errorHandler';
import KamokuSelect from './KamokuSelect';
import HojoSelect from './HojoSelect';
import { subscribeJournalListRefresh } from '../utils/journalListSync';

interface JournalEntry {
  denpyou_id: number;
  denpyou_no: string;
  kichou_date: string;
  tekiyou: string;
  karikata_kamoku_id: number | null;
  karikata_kamoku_name: string;
  karikata_hojo_id: number | null;
  karikata_hojo_name: string;
  karikata_kingaku: number;
  kashikata_kamoku_id: number | null;
  kashikata_kamoku_name: string;
  kashikata_hojo_id: number | null;
  kashikata_hojo_name: string;
  kashikata_kingaku: number;
  kaikei_kubun: number;
  torihiki_kubun: number;
  tekiyou_id: number | null;
}

interface Kamoku {
  id: number;
  kamoku: string;
  kamoku_code: string;
  kamoku_yomi?: string | null;
  hojo_kamoku_ari?: boolean;
}

interface EditFormData {
  id: number;
  kihyoubi: string;
  karikata_kamoku_id: number | null;
  karikata_hojo_id: number | null;
  karikata_kingaku: number;
  kashikata_kamoku_id: number | null;
  kashikata_hojo_id: number | null;
  kashikata_kingaku: number;
  comment: string;
  kaikei_kubun: number;
  torihiki_kubun: number;
}

interface ClassicJournalDisplayProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  selectedYear?: number;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const formatNumber = (value: number) => value.toLocaleString('ja-JP');

/** 借方または貸方に「支払資金」を含む科目がある行（一覧では既定非表示にできる） */
function journalRowInvolvesShiharaiShikin(
  entry: JournalEntry,
  kamokuById: Map<number, Kamoku>,
): boolean {
  const debitName =
    entry.karikata_kamoku_name ||
    (entry.karikata_kamoku_id != null ? kamokuById.get(entry.karikata_kamoku_id)?.kamoku || '' : '');
  const creditName =
    entry.kashikata_kamoku_name ||
    (entry.kashikata_kamoku_id != null ? kamokuById.get(entry.kashikata_kamoku_id)?.kamoku || '' : '');
  return debitName.includes('支払資金') || creditName.includes('支払資金');
}

const ClassicJournalDisplay: React.FC<ClassicJournalDisplayProps> = ({
  onMenuSelect,
  tenantName,
  selectedYear,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [journalList, setJournalList] = useState<JournalEntry[]>([]);
  const [kamokuList, setKamokuList] = useState<Kamoku[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<EditFormData | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [suggestions, setSuggestions] = useState<JournalSuggestion[]>([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [selectedHistoryAccountId, setSelectedHistoryAccountId] = useState<number | null>(null);
  // 仕訳伝票出力用の状態
  const [voucherPaperSize, setVoucherPaperSize] = useState<'A4' | 'B5'>('A4');
  const [voucherLoading, setVoucherLoading] = useState<number | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  /** オフのとき「支払資金」を含む仕訳行を一覧から除外（既定: 非表示） */
  const [showShiharaiShikinRows, setShowShiharaiShikinRows] = useState(false);

  // Toggle single selection
  const toggleSelection = (id: number) => {
    const newDecorated = new Set(selectedEntryIds);
    if (newDecorated.has(id)) {
      newDecorated.delete(id);
    } else {
      newDecorated.add(id);
    }
    setSelectedEntryIds(newDecorated);
  };

  // Toggle all visible
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = filteredJournal.map(entry => entry.denpyou_id);
      setSelectedEntryIds(new Set(allIds));
    } else {
      setSelectedEntryIds(new Set());
    }
  };

  // Print selected vouchers
  const handlePrintSelected = async () => {
    if (selectedEntryIds.size === 0) return;

    // Create query string: entry_ids=1&entry_ids=2...
    const params = new URLSearchParams();
    selectedEntryIds.forEach(id => params.append('entry_ids', id.toString()));
    params.append('paper_size', voucherPaperSize);

    try {
      setVoucherLoading(-1); // Use -1 to indicate bulk loading
      const response = await apiFetch(`/api/reports/journal-voucher/pdf?${params.toString()}`);
      if (!response.ok) {
        const message = await getErrorMessage(response, '仕訳伝票の出力に失敗しました');
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voucher_selected_${selectedEntryIds.size}items_${voucherPaperSize}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || '仕訳伝票の出力に失敗しました');
    } finally {
      setVoucherLoading(null);
    }
  };

  // Pagination State
  const [page, setPage] = useState(0);
  const [limit] = useState(1000); // Increased to 1000 per user request
  const [totalCount, setTotalCount] = useState(0);

  const fetchJournalData = useCallback(async (targetPage = 0, append = false) => {
    if (currentTenantId == null) {
      setJournalList([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);

      const params = new URLSearchParams({
        skip: (targetPage * limit).toString(),
        limit: limit.toString(),
      });

      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      // /api/denpyou は fiscal_year クエリが無いため、日付範囲で絞る
      let effectiveFrom = dateFrom;
      let effectiveTo = dateTo;
      if (!effectiveFrom && !effectiveTo && selectedYear != null) {
        effectiveFrom = `${selectedYear}-04-01`;
        effectiveTo = `${selectedYear + 1}-03-31`;
      }
      if (effectiveFrom) params.append('date_from', effectiveFrom);
      if (effectiveTo) params.append('date_to', effectiveTo);

      const url = `/api/denpyou?${params.toString()}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        const message = await getErrorMessage(response, '仕訳データの取得に失敗しました');
        throw new Error(message);
      }
      const data = await response.json();
      const rawItems = data.items || [];

      const mapped: JournalEntry[] = rawItems.map((item: any) => ({
        denpyou_id: item.id,
        denpyou_no: item.denpyou_bangou || '',
        kichou_date: item.kihyoubi || '',
        tekiyou: item.comment || '',
        karikata_kamoku_id: item.karikata_kamoku_id ?? null,
        karikata_kamoku_name: item.karikata_kamoku_name || '',
        karikata_hojo_id: item.karikata_hojo_id ?? null,
        karikata_hojo_name: item.karikata_hojo_name || '',
        karikata_kingaku: Number(item.karikata_kingaku ?? 0),
        kashikata_kamoku_id: item.kashikata_kamoku_id ?? null,
        kashikata_kamoku_name: item.kashikata_kamoku_name || '',
        kashikata_hojo_id: item.kashikata_hojo_id ?? null,
        kashikata_hojo_name: item.kashikata_hojo_name || '',
        kashikata_kingaku: Number(item.kashikata_kingaku ?? 0),
        kaikei_kubun: Number(item.kaikei_kubun ?? 1),
        torihiki_kubun: Number(item.torihiki_kubun ?? 1),
        tekiyou_id: item.tekiyou_id ?? null,
      }));

      mapped.sort((a, b) => {
        if (a.kichou_date === b.kichou_date) {
          return b.denpyou_id - a.denpyou_id;
        }
        return a.kichou_date < b.kichou_date ? 1 : -1;
      });

      if (append) {
        setJournalList(prev => [...prev, ...mapped]);
      } else {
        setJournalList(mapped);
      }
      setTotalCount(data.total_count ?? 0);
      setPage(targetPage);
      setError(null);
    } catch (err: any) {
      setError(err?.message || '仕訳データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, apiFetch, searchTerm, dateFrom, dateTo, selectedYear, limit]);

  const fetchKamokuList = useCallback(async () => {
    if (currentTenantId == null) {
      setKamokuList([]);
      return;
    }
    try {
      const response = await apiFetch('/api/kamoku');
      if (!response.ok) {
        const message = await getErrorMessage(response, '勘定科目の取得に失敗しました');
        throw new Error(message);
      }
      const data = (await response.json());
      // Ensure data matches Kamoku interface
      const mappedData: Kamoku[] = data.map((item: any) => ({
        id: item.id,
        kamoku: item.kamoku,
        kamoku_code: item.kamoku_code,
        kamoku_yomi: item.kamoku_yomi,
        hojo_kamoku_ari: item.hojo_kamoku_ari ?? false,
      }));
      setKamokuList(mappedData || []);
    } catch (err) {
      console.error('勘定科目取得エラー:', err);
    }
  }, [apiFetch, currentTenantId]);

  const fetchJournalRef = React.useRef(fetchJournalData);
  fetchJournalRef.current = fetchJournalData;

  useEffect(() => {
    setPage(0);
    fetchJournalRef.current(0, false);
    fetchKamokuList();
  }, [fetchKamokuList, currentTenantId, selectedYear]);

  const editingRowIdRef = React.useRef<number | null>(null);
  editingRowIdRef.current = editingRowId;

  useEffect(() => {
    if (currentTenantId == null) {
      return undefined;
    }
    const refetchFromExternalSignal = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (editingRowIdRef.current != null) {
        return;
      }
      fetchJournalRef.current(0, false);
    };
    const unsubscribe = subscribeJournalListRefresh(refetchFromExternalSignal);
    const onVisibility = () => refetchFromExternalSignal();
    const onFocus = () => refetchFromExternalSignal();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [currentTenantId]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    // Load more when scrolled to bottom (within 50px)
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && (page + 1) * limit < totalCount) {
      fetchJournalData(page + 1, true);
    }
  };

  const findKamokuById = (id: number | null) =>
    id == null ? '' : kamokuList.find((k) => k.id === id)?.kamoku || '';

  const updateEditForm = (updates: Partial<EditFormData>) => {
    setEditFormData((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const kamokuMap = useMemo(() => {
    const map = new Map<number, Kamoku>();
    kamokuList.forEach((kamoku) => map.set(kamoku.id, kamoku));
    return map;
  }, [kamokuList]);

  const filteredJournal = useMemo(() => {
    if (showShiharaiShikinRows) {
      return journalList;
    }
    return journalList.filter((entry) => !journalRowInvolvesShiharaiShikin(entry, kamokuMap));
  }, [journalList, showShiharaiShikinRows, kamokuMap]);

  const hiddenShiharaiCount = useMemo(() => {
    if (showShiharaiShikinRows) {
      return 0;
    }
    return journalList.filter((entry) => journalRowInvolvesShiharaiShikin(entry, kamokuMap)).length;
  }, [journalList, showShiharaiShikinRows, kamokuMap]);

  const totalAmount = useMemo(
    () => filteredJournal.reduce((sum, entry) => sum + (entry.karikata_kingaku || 0), 0),
    [filteredJournal],
  );

  const kamokuNameMap = useMemo(() => {
    const map = new Map<string, Kamoku>();
    kamokuList.forEach((kamoku) => {
      if (kamoku.kamoku) {
        map.set(kamoku.kamoku, kamoku);
      }
    });
    return map;
  }, [kamokuList]);

  const handleEdit = (entry: JournalEntry) => {
    setEditingRowId(entry.denpyou_id);
    setEditFormData({
      id: entry.denpyou_id,
      kihyoubi: entry.kichou_date ? entry.kichou_date.slice(0, 10) : '',
      karikata_kamoku_id: entry.karikata_kamoku_id,
      karikata_hojo_id: entry.karikata_hojo_id ?? null,
      karikata_kingaku: entry.karikata_kingaku,
      kashikata_kamoku_id: entry.kashikata_kamoku_id,
      kashikata_hojo_id: entry.kashikata_hojo_id ?? null,
      kashikata_kingaku: entry.kashikata_kingaku,
      comment: entry.tekiyou,
      kaikei_kubun: entry.kaikei_kubun,
      torihiki_kubun: entry.torihiki_kubun,
    });
    setValidationError(null);
    setSuggestions([]);
    setSuggestionError(null);
    setSuggestionLoading(false);
    // 編集開始時は科目フィルタをリセット（全ての候補を表示）
    setSelectedHistoryAccountId(null);
    // 検索バーに入力がある場合はそれを使い、なければ摘要の先頭部分を使用
    // これにより、検索語に基づいた履歴候補が表示される
    const searchQuery = searchTerm.trim() || (entry.tekiyou ?? '').trim();
    setSuggestionQuery(searchQuery);
  };

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    setEditFormData(null);
    setValidationError(null);
    setSuggestionQuery('');
    setSuggestions([]);
    setSuggestionError(null);
    setSuggestionLoading(false);
    setSelectedHistoryAccountId(null);
  }, []);

  useEffect(() => {
    if (editingRowId == null) {
      return;
    }
    const stillVisible = filteredJournal.some((e) => e.denpyou_id === editingRowId);
    if (!stillVisible) {
      handleCancelEdit();
    }
  }, [filteredJournal, editingRowId, handleCancelEdit]);

  const handleSaveEdit = async () => {
    if (!editFormData) {
      return;
    }

    if (editFormData.karikata_kingaku !== editFormData.kashikata_kingaku) {
      setValidationError(
        `借方金額(${formatNumber(editFormData.karikata_kingaku)})と貸方金額(${formatNumber(
          editFormData.kashikata_kingaku,
        )})が一致していません`,
      );
      return;
    }

    if (!editFormData.kihyoubi || !editFormData.karikata_kamoku_id || !editFormData.kashikata_kamoku_id) {
      setValidationError('日付、借方科目、貸方科目は必須です');
      return;
    }

    try {
      setSaving(true);
      setValidationError(null);

      const response = await apiFetch(`/api/denpyou/${editFormData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kihyoubi: editFormData.kihyoubi,
          karikata_kamoku_id: editFormData.karikata_kamoku_id,
          karikata_hojo_id: editFormData.karikata_hojo_id,
          karikata_kingaku: editFormData.karikata_kingaku,
          kashikata_kamoku_id: editFormData.kashikata_kamoku_id,
          kashikata_hojo_id: editFormData.kashikata_hojo_id,
          kashikata_kingaku: editFormData.kashikata_kingaku,
          comment: editFormData.comment,
          kaikei_kubun: editFormData.kaikei_kubun,
          torihiki_kubun: editFormData.torihiki_kubun,
        }),
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, '更新に失敗しました');
        throw new Error(message);
      }

      handleCancelEdit();
      handleCancelEdit();
      await fetchJournalData(0, false); // Reload from start on edit to ensure consistency
    } catch (err: any) {
      setValidationError(err?.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggestionSelect = (suggestion: JournalSuggestion) => {
    if (!editFormData) {
      return;
    }

    const karikata = kamokuList.find((k) => k.kamoku === suggestion.karikata_kamoku_name);
    const kashikata = kamokuList.find((k) => k.kamoku === suggestion.kashikata_kamoku_name);

    setEditFormData((prev) => {
      if (!prev) return prev;

      // 金額のコピーロジック変更 (2026-02-09):
      // ユーザーが既に金額を入力している場合は、履歴からの金額で上書きしない
      // 常に最新のprevステートから金額を取得して維持する
      const currentAmount = prev.karikata_kingaku || prev.kashikata_kingaku || 0;
      const amountToApply = currentAmount;

      return {
        ...prev,
        karikata_kamoku_id: karikata?.id ?? prev.karikata_kamoku_id,
        kashikata_kamoku_id: kashikata?.id ?? prev.kashikata_kamoku_id,
        // 金額は変更しない（現在の値を明示的に設定）
        karikata_kingaku: amountToApply,
        kashikata_kingaku: amountToApply,
        comment: suggestion.tekiyou ?? prev.comment,
      };
    });

    if (karikata?.id) {
      setSelectedHistoryAccountId(karikata.id);
    } else if (kashikata?.id) {
      setSelectedHistoryAccountId(kashikata.id);
    }

    setSuggestionQuery((suggestion.tekiyou ?? suggestionQuery).trim());
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editFormData) {
      return;
    }
    const value = e.target.value;
    updateEditForm({ comment: value });
    setSuggestionQuery(value.trim());
  };

  // 編集モード時の履歴候補取得
  useEffect(() => {
    if (!editFormData || currentTenantId == null) {
      setSuggestions([]);
      setSuggestionLoading(false);
      setSuggestionError(null);
      return;
    }

    // クエリが空でも、勘定科目が選択されていれば履歴を引くように変更
    // const query = suggestionQuery.trim();
    // if (query.length === 0) { ... } // Removed

    let isCancelled = false;
    setSuggestionLoading(true);
    setSuggestionError(null);

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        const query = suggestionQuery.trim();
        if (query) params.append('query', query);
        params.append('limit', '20');

        // 選択された勘定科目があればフィルタに追加
        if (selectedHistoryAccountId !== null) {
          params.append('kamoku_id', selectedHistoryAccountId.toString());
        }

        const response = await apiFetch(
          `/api/journal-suggestions?${params.toString()}`,
        );
        if (isCancelled) {
          return;
        }
        if (!response.ok) {
          const message = await getErrorMessage(response, '履歴候補の取得に失敗しました');
          throw new Error(message);
        }
        const data = await response.json();
        if (!isCancelled) {
          setSuggestions(data.suggestions || []);
          setSuggestionError(null);
        }
      } catch (err: any) {
        if (!isCancelled) {
          console.error('履歴候補取得エラー:', err);
          setSuggestionError(err.message || '履歴候補の取得に失敗しました');
          setSuggestions([]);
        }
      } finally {
        if (!isCancelled) {
          setSuggestionLoading(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [apiFetch, suggestionQuery, editFormData, currentTenantId, selectedHistoryAccountId]);


  // DOM-based Enter key navigation for editing row
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (e.nativeEvent.isComposing) return; // IME check

    console.log('[DEBUG] Enter key pressed in editing row', editingRowId);

    // Only apply if we are editing a row
    if (editingRowId === null) {
      console.log('[DEBUG] editingRowId is null');
      return;
    }

    // Find the editing TR
    const editingRow = document.querySelector(`tr[data-entry-id="${editingRowId}"]`);
    if (!editingRow) {
      console.log('[DEBUG] Editing row TR not found');
      return;
    }

    // Prevent default form submission/behavior
    e.preventDefault();

    // Find all focusable inputs in order
    // Note: KamokuSelect renders an input, so we target inputs.
    // We also want to include the Save button at the end.
    const focusables = Array.from(
      editingRow.querySelectorAll('input:not([type="hidden"]):not([disabled]), button[data-testid="save-button"]:not([disabled])')
    ) as HTMLElement[];

    const target = e.target as HTMLElement;
    console.log('[DEBUG] Target:', target);

    // Special handling for KamokuSelect input which might be trapped inside a component structure,
    // but e.target should be the input itself.

    const currentIndex = focusables.indexOf(target);
    console.log('[DEBUG] Current index:', currentIndex, 'Total focusables:', focusables.length);

    if (currentIndex === -1) {
      console.log('[DEBUG] Target not in focusables list');
      // If focus is somehow lost or on a non-input, start from first input
      if (focusables.length > 0) focusables[0].focus();
      return;
    }

    if (currentIndex < focusables.length - 1) {
      console.log('[DEBUG] Moving to next field');
      // Move to next field
      focusables[currentIndex + 1].focus();
    } else {
      console.log('[DEBUG] Last field, checking for Save button');
      // If we are on the Save button (last element), trigger it
      if (target.getAttribute('data-testid') === 'save-button') {
        console.log('[DEBUG] Triggering save');
        handleSaveEdit();
      }
    }
  }, [editingRowId, handleSaveEdit]);

  // 仕訳伝票PDF出力ハンドラ
  const handleVoucherPDF = async (entryId: number) => {
    if (voucherLoading !== null) {
      return;
    }
    try {
      setVoucherLoading(entryId);
      const response = await apiFetch(
        `/api/reports/journal-voucher/pdf?entry_id=${entryId}&paper_size=${voucherPaperSize}`
      );
      if (!response.ok) {
        const message = await getErrorMessage(response, '仕訳伝票の出力に失敗しました');
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voucher_${entryId}_${voucherPaperSize}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || '仕訳伝票の出力に失敗しました');
    } finally {
      setVoucherLoading(null);
    }
  };

  const handleDelete = async (denpyouId: number, denpyouNo: string) => {
    if (deletingId !== null) {
      return;
    }
    if (!window.confirm(`伝票「${denpyouNo || denpyouId}」を削除しますか？`)) {
      return;
    }

    try {
      setDeletingId(denpyouId);
      const response = await apiFetch(`/api/denpyou/${denpyouId}`, { method: 'DELETE' });
      if (!response.ok) {
        const message = await getErrorMessage(response, '削除に失敗しました');
        throw new Error(message);
      }
      if (editingRowId === denpyouId) {
        handleCancelEdit();
      }
      await fetchJournalData(page);
    } catch (err: any) {
      setError(err?.message || '仕訳の削除に失敗しました');
    } finally {
      setDeletingId(null);
    }
  };

  const username = typeof currentUser === 'string' ? currentUser : currentUser?.username || 'admin';

  useEffect(() => {
    if (!editFormData || currentTenantId == null) {
      setSuggestions([]);
      setSuggestionError(null);
      setSuggestionLoading(false);
      return;
    }

    let isCancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        setSuggestionLoading(true);
        setSuggestionError(null);
        const params = new URLSearchParams({
          query: suggestionQuery || '',
          limit: suggestionQuery ? '20' : '30',
        });
        // 選択されている科目があればフィルタに追加
        if (selectedHistoryAccountId !== null) {
          params.append('kamoku_id', selectedHistoryAccountId.toString());
        }

        const response = await apiFetch(`/api/journal-suggestions?${params.toString()}`);
        if (!response.ok) {
          const message = await getErrorMessage(response, '候補の取得に失敗しました');
          throw new Error(message);
        }
        const data = await response.json();
        if (!isCancelled) {
          setSuggestions(data.suggestions || []);
        }
      } catch (err: any) {
        if (!isCancelled) {
          console.error('候補取得エラー:', err);
          setSuggestionError(err?.message || '候補の取得に失敗しました');
          setSuggestions([]);
        }
      } finally {
        if (!isCancelled) {
          setSuggestionLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiFetch, suggestionQuery, editFormData, currentTenantId, selectedHistoryAccountId]);

  const historyAccountOptions = useMemo(() => {
    const options: Array<{ value: number | null; label: string }> = [
      { value: null, label: '全ての科目' },
    ];

    const seen = new Set<number>();
    const pushOption = (accountId: number | null) => {
      if (accountId == null || seen.has(accountId)) {
        return;
      }
      const kamoku = kamokuMap.get(accountId);
      if (!kamoku) {
        return;
      }
      seen.add(accountId);
      options.push({
        value: kamoku.id,
        label: `${kamoku.kamoku_code ?? ''}${kamoku.kamoku_code ? ' - ' : ''}${kamoku.kamoku}`,
      });
    };

    if (editFormData) {
      pushOption(editFormData.karikata_kamoku_id);
      pushOption(editFormData.kashikata_kamoku_id);
    }

    suggestions.forEach((suggestion) => {
      const debitKamoku = kamokuNameMap.get(suggestion.karikata_kamoku_name);
      const creditKamoku = kamokuNameMap.get(suggestion.kashikata_kamoku_name);
      pushOption(debitKamoku?.id ?? null);
      pushOption(creditKamoku?.id ?? null);
    });

    const [allOption, ...rest] = options;
    rest.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    return [allOption, ...rest];
  }, [editFormData, kamokuMap, kamokuNameMap, suggestions]);

  useEffect(() => {
    const availableIds = historyAccountOptions
      .map((option) => option.value)
      .filter((value): value is number => value !== null);

    if (!editFormData) {
      if (selectedHistoryAccountId !== null) {
        setSelectedHistoryAccountId(null);
      }
      return;
    }

    if (availableIds.length === 0) {
      if (selectedHistoryAccountId !== null) {
        setSelectedHistoryAccountId(null);
      }
      return;
    }

    // 選択されている科目が候補リストにない場合のみ、最初の候補に切り替え
    // ただし、null（全ての科目）の場合はそのまま維持
    if (
      selectedHistoryAccountId !== null &&
      !availableIds.includes(selectedHistoryAccountId)
    ) {
      setSelectedHistoryAccountId(null); // 候補外の場合は「全ての科目」に戻す
      return;
    }

    // 編集開始時の自動科目選択は削除（ユーザーが明示的に選択した場合のみフィルタリング）
    // これにより、編集開始時は全ての候補が表示される
  }, [editFormData, historyAccountOptions, selectedHistoryAccountId]);

  const filteredSuggestions = useMemo(() => {
    if (!editFormData) {
      return [];
    }

    const sorted = [...suggestions].sort((a, b) => {
      if (a.kihyoubi === b.kihyoubi) {
        const scoreA = a.total_score ?? 0;
        const scoreB = b.total_score ?? 0;
        return scoreB - scoreA;
      }
      return a.kihyoubi < b.kihyoubi ? 1 : -1;
    });

    if (selectedHistoryAccountId === null) {
      return sorted;
    }

    // 科目IDベースでフィルタリング（科目名の完全一致から変更）
    return sorted.filter(
      (suggestion) => {
        // JournalSuggestion型のkarikata_kamoku_idはオプショナルなので、オプショナルチェーンでアクセス
        const debitId = (suggestion as any).karikata_kamoku_id;
        const creditId = (suggestion as any).kashikata_kamoku_id;
        return (debitId != null && debitId === selectedHistoryAccountId) ||
          (creditId != null && creditId === selectedHistoryAccountId);
      }
    );
  }, [editFormData, suggestions, selectedHistoryAccountId, kamokuMap]);

  const selectedHistoryAccountName =
    selectedHistoryAccountId != null ? kamokuMap.get(selectedHistoryAccountId)?.kamoku ?? '' : '';

  const historyContextNote = useMemo(() => {
    if (!editFormData) {
      return '修正する仕訳を選択すると履歴候補が表示されます。';
    }

    const parts: string[] = [];
    if (selectedHistoryAccountName) {
      parts.push(`表示科目: ${selectedHistoryAccountName}`);
    }
    if (editFormData.karikata_kamoku_id) {
      const debitName = kamokuMap.get(editFormData.karikata_kamoku_id)?.kamoku;
      if (debitName) {
        parts.push(`借方: ${debitName}`);
      }
    }
    if (editFormData.kashikata_kamoku_id) {
      const creditName = kamokuMap.get(editFormData.kashikata_kamoku_id)?.kamoku;
      if (creditName) {
        parts.push(`貸方: ${creditName}`);
      }
    }

    if (parts.length === 0) {
      return '摘要や科目の入力に応じて過去の仕訳履歴を表示します。';
    }

    return parts.join(' / ');
  }, [editFormData, kamokuMap, selectedHistoryAccountName]);

  const handleEditAccountChange = (
    field: 'karikata_kamoku_id' | 'kashikata_kamoku_id',
    rawValue: string,
  ) => {
    if (!editFormData) {
      return;
    }
    const accountId = rawValue === '' ? null : Number(rawValue);
    const hojoField = field === 'karikata_kamoku_id' ? 'karikata_hojo_id' : 'kashikata_hojo_id';
    // 科目変更時は補助科目をリセット
    updateEditForm({ [field]: accountId, [hojoField]: null } as Partial<EditFormData>);
    if (accountId !== null) {
      setSelectedHistoryAccountId(accountId);
    }
  };

  const handleEditHojoChange = (
    field: 'karikata_hojo_id' | 'kashikata_hojo_id',
    rawValue: string,
  ) => {
    if (!editFormData) return;
    const hojoId = rawValue === '' ? null : Number(rawValue);
    updateEditForm({ [field]: hojoId } as Partial<EditFormData>);
  };

  const handleEditAmountChange = (rawValue: string) => {
    if (!editFormData) {
      return;
    }
    const parsed = parseInt(rawValue, 10);
    const amount = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    updateEditForm({
      karikata_kingaku: amount,
      kashikata_kingaku: amount,
    });
  };

  const classicButtonStyle: React.CSSProperties = {
    padding: '6px 18px',
    backgroundColor: '#c0c0c0',
    border: '2px outset #c0c0c0',
    fontSize: '12px',
    fontFamily: 'MS UI Gothic, sans-serif',
    cursor: 'pointer',
    margin: '2px',
  };

  const classicInputStyle: React.CSSProperties = {
    padding: '4px 6px',
    border: '2px inset #c0c0c0',
    fontSize: '14px',
    fontFamily: 'MS UI Gothic, sans-serif',
    backgroundColor: 'white',
    height: '32px',
    boxSizing: 'border-box',
  };

  const classicSelectStyle: React.CSSProperties = {
    padding: '2px',
    border: '2px inset #c0c0c0',
    fontSize: '14px',
    fontFamily: 'MS UI Gothic, sans-serif',
    backgroundColor: 'white',
    height: '32px',
  };

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        minHeight: '100vh',
        fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
        fontSize: '11px',
      }}
    >
      {/* ヘッダーはApp.tsxのClassicScreenShellで提供 */}

      <div
        style={{
          display: 'flex',
          gap: '16px',
          padding: '18px',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: '1 1 66%', minWidth: 0 }}>
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #c0c0c0',
              padding: '12px',
              marginBottom: '12px',
            }}
          >
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label>
                日付（開始）:
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{ marginLeft: '6px', padding: '3px', fontSize: '11px' }}
                />
              </label>
              <label>
                日付（終了）:
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{ marginLeft: '6px', padding: '3px', fontSize: '11px' }}
                />
              </label>
              <label>
                検索:
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="摘要・科目・伝票番号・金額"
                  style={{ marginLeft: '6px', padding: '3px', fontSize: '11px', width: '200px' }}
                />
              </label>
              <button
                onClick={() => fetchJournalData(0, false)}
                style={{
                  padding: '5px 14px',
                  backgroundColor: '#c0c0c0',
                  border: '2px outset #c0c0c0',
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.border = '2px inset #c0c0c0';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px outset #c0c0c0';
                }}
              >
                検索
              </button>
              <label
                style={{
                  marginLeft: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title="借方または貸方に「支払資金」を含む科目がある仕訳行を一覧に含めます（既定は非表示）"
              >
                <input
                  type="checkbox"
                  checked={showShiharaiShikinRows}
                  onChange={(e) => setShowShiharaiShikinRows(e.target.checked)}
                />
                支払資金を含む行を表示
              </label>
              <span style={{ marginLeft: '20px', borderLeft: '1px solid #999', paddingLeft: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label>
                  伝票出力サイズ:
                  <select
                    value={voucherPaperSize}
                    onChange={(e) => setVoucherPaperSize(e.target.value as 'A4' | 'B5')}
                    style={{ marginLeft: '6px', padding: '3px', fontSize: '11px' }}
                  >
                    <option value="A4">A4</option>
                    <option value="B5">B5</option>
                  </select>
                </label>
                <button
                  onClick={handlePrintSelected}
                  disabled={selectedEntryIds.size === 0 || voucherLoading !== null}
                  style={{
                    padding: '5px 14px',
                    backgroundColor: selectedEntryIds.size > 0 ? '#339966' : '#cccccc',
                    color: 'white',
                    border: '2px outset',
                    borderColor: selectedEntryIds.size > 0 ? '#339966' : '#cccccc',
                    cursor: selectedEntryIds.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                >
                  {voucherLoading === -1 ? '出力中...' : `選択した伝票を印刷 (${selectedEntryIds.size})`}
                </button>
              </span>
            </div>


          </div>

          {error && (
            <div
              style={{
                backgroundColor: '#ffe0e0',
                color: '#cc0000',
                padding: '10px',
                marginBottom: '12px',
                border: '1px solid #cc0000',
              }}
            >
              エラー: {error}
            </div>
          )}

          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #c0c0c0',
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
            onScroll={handleScroll}
          >
            {loading && journalList.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e0e0e0' }}>
                    <th style={{ padding: '6px 4px', borderBottom: '2px solid #808080', textAlign: 'center', width: '30px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      <input
                        type="checkbox"
                        checked={filteredJournal.length > 0 && selectedEntryIds.size === filteredJournal.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', width: '88px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      記帳日
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      借方科目
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', width: '90px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      借方補助
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'right', width: '100px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      金額
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      貸方科目
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', width: '90px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      貸方補助
                    </th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>摘要</th>
                    <th style={{ padding: '6px', borderBottom: '2px solid #808080', textAlign: 'center', width: '120px', position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJournal.map((entry) => {
                    const isEditing = editingRowId === entry.denpyou_id && editFormData;
                    const amountInputValue =
                      isEditing && editFormData && editFormData.karikata_kingaku != null
                        ? String(editFormData.karikata_kingaku)
                        : '';
                    const isSelected = selectedEntryIds.has(entry.denpyou_id);
                    return (
                      <React.Fragment key={entry.denpyou_id}>
                        {isEditing && editFormData ? (
                          <tr data-entry-id={entry.denpyou_id} style={{ backgroundColor: '#fff7c2' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelection(entry.denpyou_id)}
                                disabled
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              {validationError && (
                                <div
                                  style={{
                                    backgroundColor: '#ffe0e0',
                                    border: '1px solid #cc0000',
                                    color: '#8b0000',
                                    padding: '4px',
                                    marginBottom: '4px',
                                    fontSize: '10px',
                                  }}
                                >
                                  {validationError}
                                </div>
                              )}
                              <input
                                type="date"
                                value={editFormData.kihyoubi}
                                onChange={(e) =>
                                  updateEditForm({
                                    kihyoubi: e.target.value.slice(0, 10),
                                  })
                                }
                                onKeyDown={handleKeyDown}
                                style={{ ...classicInputStyle, width: '100%' }}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              <KamokuSelect
                                kamokuList={kamokuList as any[]}
                                value={editFormData.karikata_kamoku_id}
                                onChange={(value) => handleEditAccountChange('karikata_kamoku_id', value)}
                                onFocus={() => {
                                  if (editFormData.karikata_kamoku_id) {
                                    setSelectedHistoryAccountId(editFormData.karikata_kamoku_id);
                                  }
                                }}
                                onKeyDown={handleKeyDown}
                                style={{ width: '100%', height: '32px' }}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              <HojoSelect
                                kamokuId={editFormData.karikata_kamoku_id}
                                value={editFormData.karikata_hojo_id}
                                onChange={(value) => handleEditHojoChange('karikata_hojo_id', value)}
                                apiFetch={apiFetch as any}
                                currentTenantId={currentTenantId || undefined}
                                hasHojo={!!kamokuList.find(k => k.id === editFormData.karikata_kamoku_id)?.hojo_kamoku_ari}
                                style={{ width: '100%', height: '32px' }}
                                onKeyDown={handleKeyDown}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', textAlign: 'right' }}>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={amountInputValue}
                                onChange={(e) => handleEditAmountChange(e.target.value)}
                                onKeyDown={handleKeyDown}
                                style={{ ...classicInputStyle, width: '100%', textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              <KamokuSelect
                                kamokuList={kamokuList as any[]}
                                value={editFormData.kashikata_kamoku_id}
                                onChange={(value) => handleEditAccountChange('kashikata_kamoku_id', value)}
                                onFocus={() => {
                                  if (editFormData.kashikata_kamoku_id) {
                                    setSelectedHistoryAccountId(editFormData.kashikata_kamoku_id);
                                  }
                                }}
                                onKeyDown={handleKeyDown}
                                style={{ width: '100%', height: '32px' }}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              <HojoSelect
                                kamokuId={editFormData.kashikata_kamoku_id}
                                value={editFormData.kashikata_hojo_id}
                                onChange={(value) => handleEditHojoChange('kashikata_hojo_id', value)}
                                apiFetch={apiFetch as any}
                                currentTenantId={currentTenantId || undefined}
                                hasHojo={!!kamokuList.find(k => k.id === editFormData.kashikata_kamoku_id)?.hojo_kamoku_ari}
                                style={{ width: '100%', height: '32px' }}
                                onKeyDown={handleKeyDown}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              <input
                                type="text"
                                data-testid="comment-input"
                                value={editFormData.comment}
                                onChange={handleCommentChange}
                                onKeyDown={handleKeyDown}
                                placeholder="摘要を入力"
                                style={{ ...classicInputStyle, width: '100%' }}
                              />
                            </td>
                            <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button
                                  data-testid="save-button"
                                  onClick={handleSaveEdit}
                                  onKeyDown={handleKeyDown}
                                  disabled={saving}
                                  style={{
                                    padding: '4px 10px',
                                    backgroundColor: saving ? '#a0a0a0' : '#0066cc',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    opacity: saving ? 0.7 : 1,
                                    fontSize: '11px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {saving ? '保存中' : '保存'}
                                </button>
                                <button
                                  data-testid="cancel-button"
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#888',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    fontSize: '11px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  取消
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.denpyou_id, entry.denpyou_no || entry.denpyou_id.toString())}
                                  disabled={saving || deletingId === entry.denpyou_id}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: deletingId === entry.denpyou_id ? '#a0a0a0' : '#cc3333',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    opacity: deletingId === entry.denpyou_id ? 0.6 : 1,
                                    fontSize: '10px',
                                    cursor: (saving || deletingId === entry.denpyou_id) ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {deletingId === entry.denpyou_id ? '削除中' : '削除'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr data-entry-id={entry.denpyou_id} style={{ backgroundColor: '#ffffff' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelection(entry.denpyou_id)}
                              />
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              {entry.kichou_date ? entry.kichou_date.slice(0, 10) : '-'}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              {entry.karikata_kamoku_name || findKamokuById(entry.karikata_kamoku_id)}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', fontSize: '10px', color: '#666' }}>
                              {entry.karikata_hojo_name || ''}
                            </td>
                            <td
                              style={{
                                padding: '8px',
                                borderBottom: '1px solid #e0e0e0',
                                textAlign: 'right',
                              }}
                            >
                              ¥{formatNumber(entry.karikata_kingaku || entry.kashikata_kingaku || 0)}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              {entry.kashikata_kamoku_name || findKamokuById(entry.kashikata_kamoku_id)}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0', fontSize: '10px', color: '#666' }}>
                              {entry.kashikata_hojo_name || ''}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                              {entry.tekiyou || '（未入力）'}
                            </td>
                            <td style={{ padding: '6px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button
                                  data-testid="edit-button"
                                  data-entry-id={entry.denpyou_id}
                                  onClick={() => handleEdit(entry)}
                                  disabled={editingRowId !== null}
                                  style={{
                                    padding: '3px 10px',
                                    backgroundColor: editingRowId !== null ? '#cccccc' : '#0066cc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: editingRowId !== null ? 'not-allowed' : 'pointer',
                                    fontSize: '11px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  修正
                                </button>
                                <button
                                  data-testid="delete-button"
                                  data-entry-id={entry.denpyou_id}
                                  onClick={() => handleDelete(entry.denpyou_id, entry.denpyou_no)}
                                  disabled={editingRowId !== null || deletingId === entry.denpyou_id}
                                  style={{
                                    padding: '3px 8px',
                                    backgroundColor:
                                      editingRowId !== null || deletingId === entry.denpyou_id ? '#cccccc' : '#cc3333',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor:
                                      editingRowId !== null || deletingId === entry.denpyou_id
                                        ? 'not-allowed'
                                        : 'pointer',
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {deletingId === entry.denpyou_id ? '削除中' : '削除'}
                                </button>
                                <button
                                  data-testid="voucher-button"
                                  data-entry-id={entry.denpyou_id}
                                  onClick={() => handleVoucherPDF(entry.denpyou_id)}
                                  disabled={voucherLoading !== null}
                                  style={{
                                    padding: '3px 8px',
                                    backgroundColor: voucherLoading === entry.denpyou_id ? '#a0a0a0' : '#339966',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: voucherLoading !== null ? 'not-allowed' : 'pointer',
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={`仕訳伝票を${voucherPaperSize}サイズでPDF出力`}
                                >
                                  {voucherLoading === entry.denpyou_id ? '出力中' : `伝票(${voucherPaperSize})`}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {filteredJournal.length === 0 && !loading && (
                    <tr>
                      <td colSpan={9} style={{ padding: '18px', textAlign: 'center', color: '#777' }}>
                        表示できる仕訳がありません。
                        {journalList.length > 0 && hiddenShiharaiCount > 0 && !showShiharaiShikinRows ? (
                          <span style={{ display: 'block', marginTop: '8px', fontSize: '11px', color: '#555' }}>
                            読み込み済みのうち {hiddenShiharaiCount}{' '}
                            件は「支払資金」を含む行のため非表示です。上の「支払資金を含む行を表示」にチェックを入れてください。
                          </span>
                        ) : null}
                        {selectedYear != null && !dateFrom && !dateTo ? (
                          <span style={{ display: 'block', marginTop: '8px', fontSize: '11px' }}>
                            （{selectedYear}年度＝{selectedYear}/04/01〜{selectedYear + 1}/03/31
                            の範囲で検索しています。日付（開始・終了）を空にすると上記が適用されます。他年度の伝票は日付またはヘッダーの対象年度を変更してください。）
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {loading && journalList.length > 0 && (
              <div style={{ padding: '10px', textAlign: 'center', backgroundColor: '#f0f0f0', fontSize: '11px' }}>
                追加データを読み込み中...
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: '10px',
              fontSize: '11px',
              color: '#555',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>
                表示件数: {filteredJournal.length} / 全{totalCount}件
                {totalCount > journalList.length ? '（スクロールで追加読込）' : ''}
                {hiddenShiharaiCount > 0 && !showShiharaiShikinRows ? (
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    （うち支払資金行 {hiddenShiharaiCount} 件は非表示）
                  </span>
                ) : null}
              </span>
              <button
                onClick={() => onMenuSelect('journal-entry')}
                style={{
                  padding: '5px 16px',
                  backgroundColor: '#0066cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                ＋ 新規仕訳入力
              </button>
            </div>
            <span>合計金額: ¥{formatNumber(totalAmount)}</span>
          </div>
        </div>

        {/* 右側履歴パネル */}
        <div
          style={{
            flex: '0 0 34%',
            position: 'sticky',
            top: '92px',
            alignSelf: 'flex-start',
          }}
        >
          <JournalHistorySidePanel
            suggestions={filteredSuggestions}
            suggestionLoading={suggestionLoading}
            suggestionError={suggestionError}
            accountOptions={historyAccountOptions}
            selectedAccountId={selectedHistoryAccountId}
            selectedAccountName={selectedHistoryAccountName}
            onAccountChange={(accountId) => setSelectedHistoryAccountId(accountId)}
            onSuggestionSelect={handleSuggestionSelect}
            contextNote={historyContextNote}
            apiFetch={apiFetch}
            currentTenantId={currentTenantId}
            kamokuList={kamokuList}
            activeLineDebitAccountId={editFormData?.karikata_kamoku_id ?? null}
            activeLineCreditAccountId={editFormData?.kashikata_kamoku_id ?? null}
          />
        </div>

      </div>
    </div>
  );
};

export default ClassicJournalDisplay;
